import { API_BASE as BASE } from '../lib/env';

export interface SessionListItem {
  sessionId: string;
  startedAt: string;
  endedAt: string;
  pointCount: number;
  distanceM: number;
  avgRsrp: number | null;
}

export interface SessionMeasurementRow {
  ts: string | null;
  lat: number | null;
  lon: number | null;
  rsrp: number | null;
  rsrq: number | null;
  sinr: number | null;
  ci: number | string | null;
  pci: number | null;
  tac: number | null;
  earfcn: number | null;
  networkGeneration: string | null;
  timingAdvance: number | null;
  mcc: string | null;
  mnc: string | null;
  downloadMbps: number | null;
  uploadMbps: number | null;
  latencyMs: number | null;
  mos: number | null;
  operator: string | null;
}

export interface SessionPath {
  type: 'FeatureCollection';
  features: Array<{ type: 'Feature'; geometry: { type: 'LineString'; coordinates: [number, number][] }; properties: Record<string, unknown> }>;
  summary: {
    sessionId?: string;
    pointCount: number;
    distanceM: number;
    durationMs?: number;
    startedAt?: string;
    endedAt?: string;
    bbox?: unknown;
  };
}

export async function listSessions(): Promise<SessionListItem[]> {
  const r = await fetch(`${BASE}/sessions`);
  if (!r.ok) throw new Error(`/sessions ${r.status}`);
  return r.json();
}

export async function fetchSessionPath(sessionId: string): Promise<SessionPath> {
  const r = await fetch(`${BASE}/sessions/${encodeURIComponent(sessionId)}/path`);
  if (!r.ok) throw new Error(`/sessions/${sessionId}/path ${r.status}`);
  return r.json();
}

export async function fetchSessionMeasurements(sessionId: string): Promise<SessionMeasurementRow[]> {
  const r = await fetch(`${BASE}/sessions/${encodeURIComponent(sessionId)}/measurements`);
  if (!r.ok) throw new Error(`/sessions/${sessionId}/measurements ${r.status}`);
  return r.json();
}

export interface SessionSimBreakdown {
  slotIndex: number;
  carrierName: string | null;
  mcc: string | null;
  mnc: string | null;
  /** True when this slot carried mobile data during the session. */
  wasDataSim: boolean;
  pointCount: number;
  avgRsrp: number | null;
  minRsrp: number | null;
  maxRsrp: number | null;
  avgRsrq: number | null;
  avgSinr: number | null;
  uniqueCells: number;
  /** Sample count per network generation, e.g. { "4G": 1200, "5G": 275 }. */
  generationCounts: Record<string, number> | null;
}

/**
 * Per-SIM breakdown of a session. Empty for single-SIM drives and for sessions
 * recorded before per-SIM storage existed.
 */
export async function fetchSessionSims(sessionId: string): Promise<SessionSimBreakdown[]> {
  const r = await fetch(`${BASE}/sessions/${encodeURIComponent(sessionId)}/sims`);
  if (!r.ok) throw new Error(`/sessions/${sessionId}/sims ${r.status}`);
  return r.json();
}
